<template>
  <component
    :is="btnComponent"
    :class="classes"
    v-bind="componentProps"
  >
    <template v-if="hasBtnIconLeftSlot">
      <!-- @slot Slot for icon located on the left edge of the button -->
      <slot name="btn-icon-left"/>
    </template>

    <span
      v-if="hasBtnIconLeftSlot || hasBtnIconRightSlot"
      class="btn-text d-inline-flex align-items-center"
      :class="textClasses"
    >
      <!-- @slot Default slot for button text and/or inline icon(s) -->
      <slot/>
    </span>

    <slot v-else/>
    <template v-if="hasBtnIconRightSlot">
      <!-- @slot Slot for icon located on the left edge of the button -->
      <slot name="btn-icon-right"/>
    </template>
  </component>
</template>

<script>
import { resolveComponent } from 'vue';

import {
  COLOR_LIGHT,
  COLOR_MUTED,
  SIZE_MD,
} from '../../config/constants';
import { isObject } from '../../utils';
import { validateRequiredFieldsInObject } from '../../validators/validators';
import {
  BUTTON_BASE_CLASS,
  BUTTON_COLORS,
  BUTTON_LINK_COLORS,
  BUTTON_RWD_SIZES,
  BUTTON_SIZES,
  BUTTON_STATES,
  BUTTON_STATUS_TYPES,
  BUTTON_TEXT_ALIGNMENT,
} from './config';

export default {
  name: 'DpButton',

  compatConfig: { MODE: 3 },

  props: {
    /**
     * Specifies button color
     * @values light, primary, danger
     * @default light
     * */
    color: {
      required: false,
      type: String,
      default: COLOR_LIGHT,
      validator: (value) => BUTTON_COLORS.includes(value),
    },
    /**
     * Specifies button size
     * @values sm, md, lg, {default: 'lg', xs: 'md'}
     * @default md
     * */
    size: {
      required: false,
      type: [String, Object],
      default: SIZE_MD,
      validator: (value) => validateRequiredFieldsInObject(value, null, BUTTON_SIZES),
    },
    /**
     * If provided, the button will be displayed as link with a specified color.
     * @values link, muted
     * */
    linkType: {
      required: false,
      type: String,
      default: null,
      validator: (value) => BUTTON_LINK_COLORS.includes(value),
    },
    /**
     * If provided, the button will be displayed as status button with a specified color.
     * @values primary, secondary, danger
     * */
    statusType: {
      required: false,
      type: String,
      default: null,
      validator: (value) => BUTTON_STATUS_TYPES.includes(value),
    },
    /**
     * URL value
     * */
    href: {
      required: false,
      type: String,
      default: null,
    },
    /**
     * Router link `to` property. If specified the button will become a router-link component
     * */
    to: {
      required: false,
      type: [String, Object],
      default: null,
    },
    /**
     * If true button will be displayed as block
     * @values true, false
     * */
    fullWidth: {
      required: false,
      type: Boolean,
      default: false,
    },
    /**
     * Specifies the state of the button
     * @values focus, active, disabled
     * */
    state: {
      required: false,
      type: String,
      default: null,
      validator: (value) => BUTTON_STATES.includes(value),
    },
    /**
     * Use it to specify the screen size for which the button will be displayed as block
     * @values sm, md, lg
     * */
    rwdBlock: {
      required: false,
      type: String,
      default: null,
      validator: (value) => BUTTON_RWD_SIZES.includes(value),
    },
    /**
     * Alignment of text in block button.
     * @values left, right, center
     * @default center
     * */
    textAlign: {
      required: false,
      type: String,
      default: BUTTON_TEXT_ALIGNMENT.PLACEMENT_CENTER,
      validator: (value) => Object.values(BUTTON_TEXT_ALIGNMENT).includes(value),
    },
  },

  computed: {
    componentProps() {
      if (this.btnType === 'router-link') {
        return {
          ...this.$attrs,
          to: this.to,
        };
      }

      if (this.btnType === 'a') {
        return {
          ...this.$attrs,
          href: this.href,
        };
      }

      return {
        ...this.$attrs,
        disabled: this.state === 'disabled',
      };
    },

    classes() {
      const {
        color,
        linkType,
        size,
        fullWidth,
        hasBtnIconLeftSlot,
        hasBtnIconRightSlot,
        state,
        statusType,
        rwdBlock,
        textAlign,
      } = this;

      const classes = [
        BUTTON_BASE_CLASS,
        state,
      ];

      if (size !== SIZE_MD && !isObject(size)) {
        classes.push(`btn-${size}`);
      }

      if (isObject(size) && Object.keys(size).length !== 0) {
        if (size.default) {
          classes.push(`btn-${size.default}`);
        }

        if (size.xs) {
          classes.push(`btn-${size.xs}-on-xs`);
        }
      }

      if (linkType) {
        classes.push('btn-link');
      }

      if (linkType === COLOR_MUTED) {
        classes.push(`btn-link-${linkType}`);
      }

      if (statusType) {
        classes.push(`btn-status-${statusType}`);
      }

      if (fullWidth) {
        classes.push('btn-block');
      }

      if (hasBtnIconLeftSlot && !hasBtnIconRightSlot) {
        classes.push('btn-icon-on-left');
      }

      if (hasBtnIconRightSlot && !hasBtnIconLeftSlot) {
        classes.push('btn-icon-on-right');
      }

      if (color && !linkType && !statusType) {
        classes.push(`btn-${color}`);
      }

      if (rwdBlock) {
        classes.push(`btn-${rwdBlock}-block`);
      }

      if (textAlign === BUTTON_TEXT_ALIGNMENT.PLACEMENT_LEFT) {
        classes.push(`justify-content-start`);
      }

      if (textAlign === BUTTON_TEXT_ALIGNMENT.PLACEMENT_RIGHT) {
        classes.push(`justify-content-end`);
      }

      return classes;
    },

    textClasses() {
      const { textAlign } = this;
      const classes = [];

      if (textAlign === BUTTON_TEXT_ALIGNMENT.PLACEMENT_LEFT) {
        classes.push(`mr-auto`);
      }

      if (textAlign === BUTTON_TEXT_ALIGNMENT.PLACEMENT_RIGHT) {
        classes.push(`ml-auto`);
      }

      if (textAlign === BUTTON_TEXT_ALIGNMENT.PLACEMENT_CENTER) {
        classes.push(`mx-auto`);
      }

      return classes;
    },

    btnType() {
      if (this.to) {
        return 'router-link';
      }

      if (this.href) {
        return 'a';
      }

      return 'button';
    },

    btnComponent() {
      const { btnType } = this;

      if (btnType === 'router-link') {
        return resolveComponent('router-link');
      }

      return btnType;
    },

    hasBtnIconLeftSlot() {
      return !!this.$slots['btn-icon-left'];
    },

    hasBtnIconRightSlot() {
      return !!this.$slots['btn-icon-right'];
    },
  },
};
</script>
